import { useMutation } from '@apollo/client'
import {
  CountrySelect,
  LanguageSelect,
  ModalForm,
  Select,
  TextInput,
  useForm,
  yup,
} from '@faceup/form'
import {
  convertHotlineLanguageToLanguage,
  HOTLINE_COUNTRIES_WITH_CONFIGURATION,
  HOTLINE_COUNTRY_NUMBERS_CONFIGURATION,
  HotlineLanguage,
  HotlinePhoneNumberType,
  SHORT_STRING_MAX_LENGTH,
} from '@faceup/utils'
import { graphql } from '../__generated__'
import { HotlineCountry } from '../__generated__/graphql'

const mutations = {
  CreateAutomatedHotlineOrder: graphql(`
    mutation CreateAutomatedHotlineOrder(
      $input: CreateAutomatedHotlineOrderInput!
    ) {
      createAutomatedHotlineOrder(input: $input) {
        channel {
          id
        }
      }
    }
  `),
}

const schema = yup.object({
  name: yup.string().required().max(SHORT_STRING_MAX_LENGTH),
  defaultLanguage: yup.mixed<HotlineLanguage>().oneOf(Object.values(HotlineLanguage)).required(),
  country: yup.mixed<HotlineCountry>().oneOf(Object.values(HotlineCountry)).required(),
  phoneNumberType: yup
    .mixed<HotlinePhoneNumberType>()
    .oneOf(Object.values(HotlinePhoneNumberType))
    .required(),
})

type Props = {
  open: boolean
  onClose: () => void
  institutionId: string
}

export const CreateAutomatedHotlineModal = ({ open, onClose, institutionId }: Props) => {
  const form = useForm({
    schema,
    afterSubmit: 'resetValues',
    defaultValues: {
      name: '',
      defaultLanguage: undefined,
      country: HotlineCountry.Us,
      phoneNumberType: HotlinePhoneNumberType.Landline,
    },
  })

  const getHotlineConfiguration = (
    country: HotlineCountry,
    hotlinePhoneNumberType: HotlinePhoneNumberType
  ) => HOTLINE_COUNTRY_NUMBERS_CONFIGURATION[country]?.[hotlinePhoneNumberType]

  const [createAutomatedHotline] = useMutation(mutations.CreateAutomatedHotlineOrder, {
    refetchQueries: ['ReportSourcesQuery'],
    onCompleted: () => {
      onClose()
    },
  })

  return (
    <ModalForm
      form={form}
      submitButtonText='add'
      title='Create automated hotline'
      open={open}
      onSubmit={async values => {
        if (!getHotlineConfiguration(values.country, values.phoneNumberType)) {
          form.setError('phoneNumberType', {
            message: 'This phone number type is not available in this country',
          })
          return false
        }

        const result = await createAutomatedHotline({
          variables: {
            input: {
              motherId: institutionId,
              name: values.name,
              defaultLanguage: values.defaultLanguage,
              country: values.country,
              phoneNumberType: values.phoneNumberType,
            },
          },
        })
        if (result.errors) {
          return false
        }
        return true
      }}
      onClose={onClose}
    >
      <TextInput control={form.control} name='name' label='Name' />
      <LanguageSelect
        allowedLanguages={Object.values(HotlineLanguage).map(convertHotlineLanguageToLanguage)}
        control={form.control}
        name='defaultLanguage'
        label='Language'
      />
      <CountrySelect
        control={form.control}
        name='country'
        label='Country'
        countries={HOTLINE_COUNTRIES_WITH_CONFIGURATION}
        withPhoneCode
        withAsterisk
        includeSanctionedCountries
      />
      <Select
        control={form.control}
        name='phoneNumberType'
        label='Phone number type'
        options={Object.values(HotlinePhoneNumberType).map(type => ({
          label: type,
          value: type,
          disabled: !getHotlineConfiguration(form.watch('country'), type),
        }))}
      />
    </ModalForm>
  )
}
